import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'
import {
  getCurrencies,
  getGlobalSettings,
  editGlobalSettings,
  getWhitelist,
  editWhitelist,
  getGlobalSawSettings,
  editGlobalSawSettings,
  getProductTypes,
  getNtdFields,
  getNtdValue,
  getDefaultNtdValue,
  editNtdValue,
  editDefaultNtdValue,
  exchangeEbayCode,
  getDataproviderAuthStatus,
  loginDataprovider,
} from '../api/react/system/system'
import type { CurrencyFilters, UpdateGlobalSettingsPayload, WhitelistSettings, UpdateGlobalSawSettingsPayload, ProductTypeFilters, NtdType, NtdValueParams, NtdValueByIdParams } from '../types/system.types'
import { useAuth } from './useAuth'

export const SYSTEM_KEYS = {
  currencies: (filters: CurrencyFilters) => ['currencies', filters] as const,
  globalSettings: () => ['globalSettings'] as const,
  whitelist: () => ['whitelist'] as const,
  sawSettings: () => ['sawSettings'] as const,
  productTypes: (filters: ProductTypeFilters) => ['productTypes', filters] as const,
  ntdFields: (type: NtdType) => ['ntdSecretFields', type] as const,
  ntdValue: (params: NtdValueByIdParams) => ['ntdSecretField', params.id, params.fields] as const,
  ntdDefaultValue: (params: NtdValueParams) => ['ntdSecretField', params.fields] as const,
}

// GET /settings/currencies
export const useCurrencies = (filters: CurrencyFilters = {}) => {
  return useQuery({
    queryKey: SYSTEM_KEYS.currencies(filters),
    queryFn: () => getCurrencies(filters),
  })
}

// GET /settings/global
export const useGlobalSettings = () => {
  const { permissions } = useAuth()
  return useQuery({
    queryKey: SYSTEM_KEYS.globalSettings(),
    queryFn: () => getGlobalSettings(),
    enabled: !permissions.isMember
  })
}

// PATCH /settings/global
export const useEditGlobalSettings = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: UpdateGlobalSettingsPayload) => editGlobalSettings(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: SYSTEM_KEYS.globalSettings() }),
  })
}

// GET /settings/whitelist
export const useWhitelist = () => {
  return useQuery({
    queryKey: SYSTEM_KEYS.whitelist(),
    queryFn: () => getWhitelist(),
  })
}

// PATCH /settings/whitelist
export const useEditWhitelist = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: WhitelistSettings) => editWhitelist(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: SYSTEM_KEYS.whitelist() }),
  })
}

// GET /settings/saw
export const useGlobalSawSettings = () => {
  return useQuery({
    queryKey: SYSTEM_KEYS.sawSettings(),
    queryFn: () => getGlobalSawSettings(),
  })
}

// PATCH /settings/saw
export const useEditGlobalSawSettings = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: UpdateGlobalSawSettingsPayload) => editGlobalSawSettings(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: SYSTEM_KEYS.sawSettings() }),
  })
}

// GET /settings/product-types
export const useProductTypes = (filters: ProductTypeFilters, enabled = true) => {
  return useQuery({
    queryKey: SYSTEM_KEYS.productTypes(filters),
    queryFn: () => getProductTypes(filters),
    enabled,
  })
}

// GET /ntd/fields
export const useNtdFields = (type: NtdType, onSuccess?: () => void) => {
  const called = useRef(false)
  const query = useQuery({
    queryKey: SYSTEM_KEYS.ntdFields(type),
    queryFn: () => getNtdFields(type),
  })

  useEffect(() => {
    if (query.isSuccess && !called.current) {
      called.current = true
      onSuccess?.()
    }
  }, [query.isSuccess, onSuccess])

  return query
}

// GET /ntd/value (by id)
export const useNtdValue = (params: NtdValueByIdParams, enabled: boolean) => {
  return useQuery({
    queryKey: SYSTEM_KEYS.ntdValue(params),
    queryFn: () => getNtdValue(params),
    enabled,
  })
}

// GET /ntd/value (default)
export const useNtdDefaultValue = (params: NtdValueParams, enabled: boolean) => {
  return useQuery({
    queryKey: SYSTEM_KEYS.ntdDefaultValue(params),
    queryFn: () => getDefaultNtdValue(params),
    enabled,
  })
}

// PATCH /ntd/value (by id)
export const useEditNtdValue = () => {
  return useMutation({
    mutationFn: (data: { [key: string]: string; id: string; type: NtdType }) => editNtdValue(data),
  })
}

// PATCH /ntd/value (default)
export const useEditDefaultNtdValue = () => {
  return useMutation({
    mutationFn: (data: { type: NtdType; [key: string]: string }) => editDefaultNtdValue(data),
  })
}

// GET /settings/dataprovider/auth/
export const useDataproviderAuthStatus = () => {
  return useQuery({
    queryKey: ['dataproviderAuthStatus'],
    queryFn: () => getDataproviderAuthStatus(),
  })
}

// POST /settings/dataprovider/auth/
export const useLoginDataprovider = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { username: string; password: string }) => loginDataprovider(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['dataproviderAuthStatus'] }),
  })
}

// POST /ntd/ebay-exchange
export const useExchangeEbayCode = () => {
  return useMutation({
    mutationFn: (data: { code: string; member_id?: string; brand_id?: string }) => exchangeEbayCode(data),
  })
}

// GET /versions.json (app version info, no-cache, fetched with plain axios — not the API client)
export const useAppVersions = () => {
  return useQuery({
    queryKey: ['appVersions'],
    queryFn: async () => {
      const axios = (await import('axios')).default
      const res = await axios.get<{ version: string; date: string; changes: string[] }[]>('/versions.json', {
        headers: { 'Cache-Control': 'no-cache' },
      })
      // Dev server SPA fallback may return HTML — guard against non-array responses
      return Array.isArray(res.data) ? res.data : []
    },
    staleTime: 5 * 60 * 1000,
  })
}

// GET plugin versions.json from S3 (fetched with plain axios — not the API client)
export const usePluginVersions = (enabled: boolean) => {
  return useQuery({
    queryKey: ['pluginVersions'],
    queryFn: async () => {
      const axios = (await import('axios')).default
      const res = await axios.get<{ version: string; date: string; changes: string[] }[]>(
        'https://anglerfish-plugin.s3.eu-central-1.amazonaws.com/versions.json',
        { headers: { 'Cache-Control': 'no-cache' } }
      )
      return res.data
    },
    enabled,
  })
}
